import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

type Props = {
  plans: any[];
  receipts: any[];
  paymentFilter: string;
  setPaymentFilter: (value: string) => void;
  onView: (payment: any) => void;
  onEdit: (payment: any) => void;
  onDelete: (payment: any) => void;
  currency?: string;
};

const stickyFooterCellStyle = {
  position: "sticky" as const,
  bottom: 0,
  backgroundColor: "#fff",
  zIndex: 2,
  boxShadow: "0 -1px 0 #dee2e6",
};

// Boutton protéger pour modifier un nouveau paiement
const ProtectedBtnUpdatePayment = withPermission(
  (props: any) => (
    <button
      className="btn btn-outline-warning btn-sm"
      onClick={() => props.onEdit(props.r)}
    >
      <i className="ti ti-pencil" />
    </button>
  ),
  [buildPermission(Resource.PAYMENT, Action.UPDATE)],
  "masquer",
);

// Boutton protéger pour supprimer un nouveau paiement
const ProtectedBtnDeletePayment = withPermission(
  (props: any) => (
    <button
      className="btn btn-outline-danger btn-sm"
      onClick={() => props.onDelete(props.r)}
    >
      <i className="ti ti-trash" />
    </button>
  ),
  [buildPermission(Resource.PAYMENT, Action.DELETE)],
  "masquer",
);

const StudentPaymentsTable = ({
  plans,
  receipts,
  paymentFilter,
  setPaymentFilter,
  onView,
  onEdit,
  onDelete,
  currency = "FCFA",
}: Props) => {
  const filteredReceipts =
    paymentFilter === "Tous"
      ? receipts
      : receipts.filter((receipt) =>
          receipt?.repartitions?.some(
            (p: any) => p?.payment_plan?.id === paymentFilter,
          ),
        );

  const sumReceipts = useMemo(() => {
    return filteredReceipts.reduce(
      (s: number, r: any) =>
        s +
        ((paymentFilter === "Tous"
          ? r?.repartitions
          : r?.repartitions?.filter(
              (p: any) => p?.payment_plan?.id === paymentFilter,
            )
        )?.reduce((acc: number, p: any) => acc + Number(p?.amount || 0), 0) ||
          0),
      0,
    );
  }, [filteredReceipts, paymentFilter]);

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h6 className="mb-0">Paiements</h6>
        <div className="d-flex align-items-center gap-2">
          <select
            className="form-select form-select-sm"
            style={{ width: 160 }}
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
          >
            <option value="Tous">Tous</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan?.PaymentPlan?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="card-body p-0">
        <div
          className="table-responsive"
          style={{ maxHeight: 520, overflowY: "auto" }}
        >
          <table className="table mb-0">
            <thead>
              <tr>
                <th>Date de réception</th>
                <th>Au total</th>
                <th>Montant</th>
                <th>Plan de paiement</th>
                <th>Information pour le destinataire</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody style={{ maxHeight: "400px", overflow: "auto" }}>
              {filteredReceipts.map((r: any) => {
                const repartitions =
                  paymentFilter === "Tous"
                    ? r?.repartitions || []
                    : (r?.repartitions || []).filter(
                        (p: any) => p?.payment_plan?.id === paymentFilter,
                      );

                const rows = repartitions.length || 1;
                const firstAlloc = repartitions[0];
                const rest = repartitions.slice(1);
                const total = repartitions.reduce(
                  (s: number, p: any) => s + Number(p?.amount || 0),
                  0,
                );

                return (
                  <Fragment key={r?.id}>
                    <tr key={`${r.id}-0`}>
                      <td rowSpan={rows}>
                        {r?.reception_date
                          ? new Date(r.reception_date).toLocaleDateString(
                              "fr-FR",
                            )
                          : "-"}
                      </td>
                      <td rowSpan={rows}>
                        {total?.toLocaleString("fr-FR") ?? "-"}
                      </td>
                      <td>
                        {firstAlloc?.amount?.toLocaleString("fr-FR") ?? "-"}
                      </td>
                      <td>{firstAlloc?.payment_plan?.label ?? "-"}</td>
                      <td rowSpan={rows}>
                        {r?.bank_account
                          ? `${r?.bank_account?.bank_name} - ${r?.bank_account?.account_number}`
                          : "-"}
                      </td>
                      <td rowSpan={rows} className="text-end">
                        <div className="btn-group">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => onView(r)}
                          >
                            <i className="ti ti-eye" />
                          </button>
                          <ProtectedBtnUpdatePayment onEdit={onEdit} r={r} />
                          <ProtectedBtnDeletePayment
                            onDelete={onDelete}
                            r={r}
                          />
                        </div>
                      </td>
                    </tr>
                    {rest.map((a: any, idx: number) => (
                      <tr key={`${r?.repartition_id}-${idx + 1}`}>
                        <td>{a?.amount?.toLocaleString("fr-FR") ?? "-"}</td>
                        <td>{a?.payment_plan?.label ?? "-"}</td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="fw-semibold" style={stickyFooterCellStyle}>
                  Somme:
                </td>
                <td style={stickyFooterCellStyle}></td>
                <td style={stickyFooterCellStyle}>
                  {sumReceipts.toLocaleString("fr-FR")}
                </td>
                <td style={stickyFooterCellStyle}></td>
                <td style={stickyFooterCellStyle}></td>
                <td className="text-end" style={stickyFooterCellStyle}>
                  <Link to="#" className="btn btn-info btn-sm">
                    Vue de l'utilisateur
                  </Link>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default withPermission(
  StudentPaymentsTable,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.PAYMENT, action),
    ),
  ],
  "masquer",
);
